import Button from "@/components/Button";
import { DialogContent, DialogFooter, DialogTitle } from "@/components/chadcn/Dialog";
import Textarea from "@/components/chadcn/TextArea";
import Dialog from "@/components/Dialog";
import { useStickyNoteMutation } from "@/data/queries";
import { useRef } from "react";
import { useForm } from "react-hook-form";

const BULLET = "• ";

type FormData = {
  stickyNote: string;
};

type StickyNoteDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  note: string;
  prescriptionId: string | undefined;
};

export const StickyNoteDialog = ({
  open,
  onOpenChange,
  note,
  prescriptionId,
}: StickyNoteDialogProps) => {
  const {
    mutateAsync: setStickyNote,
    isPending: isSavingStickyNote,
    isError: isErrorSavingStickyNote,
  } = useStickyNoteMutation();

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const defaultValues = {
    stickyNote: note || `${BULLET} `,
  };

  const { register, handleSubmit, setValue, reset } = useForm({
    defaultValues,
  });

  const { ref: registerStickyNoteRef, ...registerStickyNoteWithoutRef } = register("stickyNote");

  const onFocus = (event: React.FocusEvent<HTMLTextAreaElement>) => {
    const textarea = event.currentTarget;
    const { value } = textarea;
    textarea.setSelectionRange(value.length, value.length);
  };

  // Enter starts a new line with bulletpoint.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter") {
      return;
    }
    event.preventDefault();
    const textarea = event.currentTarget;
    const { selectionStart, selectionEnd, value } = textarea;
    const insertion = `\n${BULLET}`;
    const newValue = value.slice(0, selectionStart) + insertion + value.slice(selectionEnd);
    setValue("stickyNote", newValue);
    const caretPosition = selectionStart + insertion.length;
    textarea.setSelectionRange(caretPosition, caretPosition);
  };

  const onSubmitInternal = async (form: FormData) => {
    try {
      await setStickyNote({
        prescriptionId: prescriptionId!,
        stickyNote: form.stickyNote,
      });
      handleOpenChange(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    if (open) {
      reset(defaultValues);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex min-w-[520px] max-w-[10%] flex-col gap-y-2 bg-white">
        <DialogTitle className="text-xl">Sticky note</DialogTitle>
        <p className="text-sm text-primary/60">Team notes - not part of the clinical record.</p>
        <form className="flex flex-col gap-y-4" onSubmit={handleSubmit(onSubmitInternal)}>
          <Textarea
            ref={(e) => {
              registerStickyNoteRef(e);
              textareaRef.current = e;
            }}
            {...registerStickyNoteWithoutRef}
            autoFocus
            className="h-40 resize-none border-orange-200 bg-white"
            onKeyDown={handleKeyDown}
            onFocus={onFocus}
          />
          {isErrorSavingStickyNote && (
            <p className="my-2 text-red-600">
              There was an error submitting the note. Please contact Bloomlife Support, or try again
              later.
            </p>
          )}
          <DialogFooter>
            <div className="flex gap-2">
              <Button
                variant="neutral"
                type="button"
                onClick={() => {
                  handleOpenChange(false);
                }}
                className="w-fit border-none bg-primary/10"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                className="w-fit"
                disabled={isSavingStickyNote}
              >
                Save
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
